import { BASE } from '@arcxgame/contracts/dist/src'
import { BigNumber } from '@ethersproject/bignumber'
import { utils } from 'ethers'
import React, { useEffect, useState } from 'react'
import Loader from 'react-loader-spinner'
import { PercentageSlider } from 'src/components/PercentageSlider'
import { Select } from 'src/components/Select'
import { SelectOption } from 'src/components/Select/Select'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { cutPrecision, formatBigNumber } from 'src/utils/format/formatBigNumber'
import './Input.scss'

interface IProps<T> {
  balance?: BigNumber
  label: string
  assets: SelectOption<T>[] // eslint-disable-line @typescript-eslint/array-type
  onAssetChanged?: (asset: T) => void
  selectedAsset: T
  setAmount?: (amount: BigNumber) => void
  maxAmount?: BigNumber
  boundaryRatio?: BigNumber
  defaultValue?: BigNumber
  disabled?: boolean
  mode?: 'just-input' | 'with-slider'
  decimals?: number
  precision?: number
  loading?: boolean
}

export function Input<T>({
  balance,
  label,
  assets,
  onAssetChanged,
  setAmount,
  selectedAsset,
  defaultValue,
  maxAmount,
  precision = 4,
  decimals = 18,
  boundaryRatio = utils.parseEther('1'),
  disabled = false,
  loading = false,
  mode = 'just-input',
}: IProps<T>) {
  if (!disabled) {
    if (!setAmount) {
      throw new Error('Property setAmount is required when input is not disabled')
    }
  }
  const [value, setValue] = useState<string>(formatBigNumber(defaultValue || maxAmount, decimals))
  useEffect(() => {
    if (defaultValue) {
      setValue((prevValue) =>
        prevValue === formatBigNumber(defaultValue, decimals) || maxAmount.lt(defaultValue)
          ? prevValue
          : formatBigNumber(boundaryRatio.mul(maxAmount).div(BASE), decimals),
      )
    } else if (
      maxAmount &&
      (maxAmount.gt(0) || (balance && maxAmount.eq(0) && maxAmount.eq(balance)))
    ) {
      setValues(formatBigNumber(boundaryRatio.mul(maxAmount).div(BASE), decimals))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(maxAmount)])

  useEffect(() => {
    setValue(formatBigNumber(defaultValue, decimals))
  }, [defaultValue, decimals])

  function setValues(value: string) {
    if (disabled || loading) {
      return
    }
    setAmount(utils.parseUnits(value, decimals))
    setValue(value)
  }
  return (
    <>
      <div className="arc-input__container">
        <div className="arc-input__labeled-input">
          <p className="arc-input__standard-text arc-input__label">{label}</p>
          {loading ? (
            <Loader type="Grid" color="white" height={25} style={{ paddingTop: '0.5rem' }} />
          ) : (
            <input
              className="arc-input__grey-text"
              type="number"
              value={cutPrecision(value, precision)}
              disabled={disabled}
              onChange={(e) => {
                const value = e.target.value
                const splitValue = value.split('.')
                if (splitValue[1]?.length > decimals) {
                  const _value = `${splitValue[0]}.${splitValue[1].substr(0, decimals)}`
                  setValues(_value)
                } else if (value[0] === '0' && value[1] === '.') {
                  setValues(value)
                } else if (value.length === 0 || Number(value) <= 0) {
                  setValues('0')
                } else {
                  const wholePart = Number(splitValue[0]).toString()
                  if (splitValue.length === 1) {
                    setValues(wholePart)
                  } else {
                    setValues(`${wholePart}.${splitValue[1]}`)
                  }
                }
              }}
            />
          )}
        </div>
        <div className="arc-input__labeled-select">
          {balance && (
            <p className="arc-input__standard-text arc-input__grey-text">
              Wallet Balance: {getReadableNumber(balance, 2, decimals)}
            </p>
          )}
          <Select options={assets} value={selectedAsset} onSelect={onAssetChanged} />
        </div>
      </div>
      {mode === 'with-slider' && (
        <PercentageSlider
          label="Utilisation"
          maxValue={maxAmount}
          onChange={(value) => {
            setValue(formatBigNumber(value, decimals))
          }}
          onAfterChange={setAmount}
          value={utils.parseUnits(value, decimals)}
        />
      )}
    </>
  )
}
